import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Accessible dialog for the mailbox admin forms, built on the portal's `.modal` shell.
 * Moves focus in on open, keeps Tab inside, closes on Escape / backdrop click, and gives
 * focus back to whatever opened it. Events from the app's confirm dialog (rendered as a
 * sibling by ModalProvider) never reach this element, so Escape there does not close us.
 */
export function AdminDialog({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const first = dialogRef.current?.querySelector<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
    );
    (first ?? dialogRef.current)?.focus();
    return () => opener?.focus?.();
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    if (e.shiftKey && document.activeElement === firstItem) {
      e.preventDefault();
      lastItem.focus();
    } else if (!e.shiftKey && document.activeElement === lastItem) {
      e.preventDefault();
      firstItem.focus();
    }
  };

  return (
    <div className="modal" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        className="modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{ maxWidth: wide ? 720 : 520, maxHeight: 'calc(100vh - 2rem)', display: 'flex' }}
      >
        <div
          className="modal-content"
          style={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: 0 }}
        >
          <div className="modal-header">
            <h5 id={titleId}>{title}</h5>
          </div>
          <div className="modal-body" style={{ overflowY: 'auto', paddingTop: '1rem' }}>
            {children}
          </div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

/** Borderless wrapper so inputs inside a dialog pick up the portal's `.form-card` field styling. */
export function FieldStack({ children }: { children: ReactNode }) {
  return (
    <div
      className="form-card"
      style={{ boxShadow: 'none', padding: 0, margin: 0, maxWidth: 'none' }}
    >
      {children}
    </div>
  );
}
